import { useState, type FormEvent } from "react";

import { organizations, platform, type Organization } from "../lib/api.js";
import { useAuth } from "../lib/auth-context.js";
import { useLiveData } from "../lib/use-live-data.js";

/**
 * The organizations this account belongs to.
 *
 * Top level of Router Report: an organization owns a tree of sites and racks,
 * its devices, its users and its alert rules, and nothing crosses between two
 * of them.
 */

const REFRESH_MS = 120_000;

export function OrganizationsPage() {
  const { data, error, reload } = useLiveData(() => organizations.list(), REFRESH_MS);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newOwner, setNewOwner] = useState("");
  /**
   * An organization just created that the caller is not in.
   *
   * Held in state because otherwise creating one as a platform operator looks
   * exactly like a failure: the form closes, the list reloads, and the list is
   * scoped to organizations you belong to -- so the thing you just made cannot
   * appear in it. The row of small print under the form explained why, but
   * only to someone who read it before pressing the button and remembered
   * afterwards.
   */
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  const { user } = useAuth();

  const canManage = (org: Organization) => org.role === "owner" || org.role === "admin";

  const submitRename = async (org: Organization, event: FormEvent) => {
    event.preventDefault();
    setActionError(null);
    try {
      await organizations.rename(org.id, draft);
      setRenaming(null);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not rename");
    }
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setActionError(null);
    try {
      const { id, joined } = await organizations.create(newName, newOwner || null);
      // Only switch into it if we are actually in it. A platform admin
      // creating an organization for someone else is deliberately not a
      // member, and activating one you do not belong to fails.
      if (joined) {
        await organizations.activate(id);
        window.location.assign("/");
      } else {
        setCreated({ id, name: newName.trim() });
        setCreating(false);
        setNewName("");
        setNewOwner("");
        reload();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not create organization");
    }
  };

  /** Joins an organization just created, so it becomes reachable. */
  const joinCreated = async () => {
    if (!created) return;
    setActionError(null);
    try {
      await platform.join(created.id);
      setCreated(null);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not join");
    }
  };

  const switchTo = async (org: Organization) => {
    await organizations.activate(org.id);
    window.location.assign("/");
  };

  return (
    <div>
      <h1 style={{ fontSize: 15, margin: "0 0 6px", letterSpacing: "0.14em" }}>&gt; ORGANIZATIONS</h1>
      <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "0 0 14px", lineHeight: 1.5 }}>
        Each organization has its own sites, devices, users and alert rules.
      </p>

      {actionError && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${actionError}`}</p>}
      {error && !data && <p style={{ color: "var(--status-critical)" }}>{`!! ${error}`}</p>}
      {!data && !error && <p style={{ color: "var(--text-muted)" }}>LOADING...</p>}

      {data && (
        <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)", marginBottom: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                {["NAME", "YOUR ROLE", "DEVICES", "PEOPLE", ""].map((h, i) => (
                  <th key={h || `a${i}`} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.organizations.map((org) => {
                const active = org.id === data.activeId;
                return (
                  <tr key={org.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                    <td style={td}>
                      {renaming === org.id ? (
                        <form onSubmit={(e) => void submitRename(org, e)} style={{ display: "flex", gap: 6 }}>
                          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} style={input} />
                          <button type="submit" style={smallButton}>[ SAVE ]</button>
                          <button type="button" onClick={() => setRenaming(null)} style={smallButton}>[ X ]</button>
                        </form>
                      ) : (
                        <>
                          {org.name}
                          {active && (
                            <span style={{ color: "var(--success-text)", fontSize: 11 }}> (active)</span>
                          )}
                          <span style={{ display: "block", color: "var(--text-muted)", fontSize: 11 }}>
                            {org.slug}
                          </span>
                        </>
                      )}
                    </td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{org.role.toUpperCase()}</td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{org.device_count}</td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>{org.member_count}</td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {!active && (
                        <>
                          <button onClick={() => void switchTo(org)} style={smallButton}>[ SWITCH TO ]</button>{" "}
                        </>
                      )}
                      {canManage(org) && renaming !== org.id && (
                        <button
                          onClick={() => { setRenaming(org.id); setDraft(org.name); }}
                          style={smallButton}
                        >
                          [ RENAME ]
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {created && (
        <div
          style={{
            border: "1px solid var(--status-warning)",
            background: "color-mix(in srgb, var(--status-warning) 10%, var(--surface-1))",
            padding: 14,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <div style={{ color: "var(--status-warning)", letterSpacing: "0.1em", marginBottom: 8 }}>
            CREATED {created.name.toUpperCase()}
          </div>
          <p style={{ margin: "0 0 10px", color: "var(--text-secondary)", lineHeight: 1.6 }}>
            It has no members yet, so it isn't listed above. Find it on the platform page.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => void joinCreated()} style={smallButton}>[ JOIN AS OWNER ]</button>
            <a href="/platform" style={{ ...smallButton, textDecoration: "none", display: "inline-block" }}>
              [ ADD SOMEONE ELSE ]
            </a>
            <button onClick={() => setCreated(null)} style={smallButton}>[ DISMISS ]</button>
          </div>
        </div>
      )}

      {creating ? (
        <form onSubmit={(e) => void create(e)} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <div>
            <label style={label}>&gt; ORGANIZATION NAME</label>
            <input autoFocus required value={newName} onChange={(e) => setNewName(e.target.value)} style={{ ...input, width: 260 }} />
          </div>
          {user?.isPlatformAdmin && (
            <div>
              <label style={label}>&gt; FIRST OWNER (OPTIONAL)</label>
              <input
                type="email"
                value={newOwner}
                onChange={(e) => setNewOwner(e.target.value)}
                placeholder="existing account email"
                style={{ ...input, width: 240 }}
              />
            </div>
          )}
          <button type="submit" style={{ ...smallButton, padding: "8px 14px" }}>[ CREATE ]</button>
          <button type="button" onClick={() => setCreating(false)} style={{ ...smallButton, padding: "8px 14px" }}>
            [ CANCEL ]
          </button>
        </form>
      ) : (
        <button onClick={() => setCreating(true)} style={{ ...smallButton, padding: "6px 12px" }}>
          [ + NEW ORGANIZATION ]
        </button>
      )}

      <p style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 14, lineHeight: 1.5 }}>
        {user?.isPlatformAdmin
          ? "You won't be added to it. Name a first owner, or add people later from the platform page. "
          : "You become the owner of an organization you create. "}
        There is no way to
        delete one from here — doing so would destroy every device and every
        metric ever recorded in it, which is not an action to offer beside a
        rename button.
      </p>
    </div>
  );
}

const th: React.CSSProperties = { padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 14px" };
const input: React.CSSProperties = {
  padding: "7px 9px", fontSize: 13, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)",
};
const label: React.CSSProperties = {
  display: "block", fontSize: 11, color: "var(--text-secondary)", marginBottom: 4, letterSpacing: "0.08em",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em",
};
